import React, { useEffect, useState } from "react";
import { Menu, X, ShoppingBag } from "lucide-react";
import { LOGO } from "../../data/content";
import { useCart } from "../../context/CartContext";

const LINKS = [
  { label: "Story", id: "story" },
  { label: "Resume", id: "resume" },
  { label: "Journey", id: "journey" },
  { label: "Highlights", id: "highlights" },
  { label: "Shop", id: "shop" },
  { label: "Partners", id: "partnerships" },
];

const CartButton = ({ count, onClick, variant }) => (
  <button data-testid={`cart-button-${variant}`} onClick={onClick} className="relative text-cream hover:text-stone transition-colors">
    <ShoppingBag size={22} />
    {count > 0 && (
      <span data-testid={`cart-count-${variant}`} className="absolute -top-2 -right-2 bg-stone text-matte text-[0.6rem] font-bold min-w-[18px] h-[18px] rounded-full flex items-center justify-center px-1">
        {count}
      </span>
    )}
  </button>
);

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { count, setOpen: setCartOpen } = useCart();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled ? "bg-matte/85 backdrop-blur-xl border-b border-silver/10 py-3" : "py-6"
      }`}
    >
      <div className="px-6 md:px-12 lg:px-16 flex items-center justify-between">
        <button
          data-testid="nav-logo"
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          className="flex items-center gap-3 text-left leading-none"
        >
          <img
            src={LOGO}
            alt="JT"
            className="h-12 md:h-14 w-auto"
          />
          <span className="block">
            <span className="heading text-2xl md:text-3xl block">JARON THAMES</span>
            <span className="font-accent text-stone text-lg md:text-xl -mt-1 block">One Of None</span>
          </span>
        </button>

        <nav className="hidden lg:flex items-center gap-8">
          {LINKS.map((l) => (
            <button
              key={l.id}
              data-testid={`nav-${l.id}`}
              onClick={() => go(l.id)}
              className="uppercase tracking-[0.2em] text-xs text-cream/70 hover:text-stone transition-colors"
            >
              {l.label}
            </button>
          ))}
          <button onClick={() => go("journey")} data-testid="nav-follow" className="btn-primary !py-3 !px-6">
            Follow
          </button>
          <CartButton count={count} onClick={() => setCartOpen(true)} variant="desktop" />
        </nav>

        <div className="lg:hidden flex items-center gap-4">
          <CartButton count={count} onClick={() => setCartOpen(true)} variant="mobile" />
          <button
            data-testid="nav-toggle"
            className="text-cream"
            onClick={() => setOpen((o) => !o)}
          >
            {open ? <X size={26} /> : <Menu size={26} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden bg-matte/95 backdrop-blur-xl border-t border-silver/10 px-6 py-6 flex flex-col gap-5">
          {LINKS.map((l) => (
            <button
              key={l.id}
              data-testid={`nav-mobile-${l.id}`}
              onClick={() => go(l.id)}
              className="uppercase tracking-[0.2em] text-sm text-cream/80 text-left"
            >
              {l.label}
            </button>
          ))}
        </div>
      )}
    </header>
  );
};
